/**
 * 儲值模組 - 服務層
 * @module prepaid/service
 * @description 處理儲值金扣款相關的業務邏輯
 */

import { supabase } from '../../services/supabaseClient';

/**
 * 儲值金扣款結果介面
 */
export interface PrepaidPaymentResult {
  success: boolean;
  message: string;
  data?: {
    order_id: string;
    order_number: string;
    customer_name: string;
    transaction_uuid: string;
    payment_breakdown: {
      total_amount: number;
      real_amount_used: number;
      bonus_amount_used: number;
      requires_invoice: boolean;
    };
    balance_info: {
      before: {
        real: number;
        bonus: number;
        total: number;
      };
      after: {
        real: number;
        bonus: number;
        total: number;
      };
    };
    low_balance_warning: boolean;
  };
  error_type?: string;
  error_details?: string;
}

/**
 * 處理訂單儲值金扣款
 * @param orderId 訂單 ID
 * @param adminId 操作管理員 ID（可選）
 * @param adminName 操作管理員名稱（可選）
 */
export async function processPrepaidPayment(
  orderId: string,
  adminId?: string,
  adminName?: string
): Promise<PrepaidPaymentResult> {
  try {
    console.log('🚀 開始呼叫儲值金扣款 API...');
    console.log('📋 訂單 ID:', orderId);
    
    const response = await fetch(
      `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/process-prepaid-payment`,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          order_id: orderId,
          admin_id: adminId,
          admin_name: adminName,
        }),
      }
    );

    console.log('📡 API 回應狀態:', response.status, response.statusText);

    // ✅ 嘗試解析 JSON 回應
    let result: PrepaidPaymentResult;
    try {
      result = await response.json();
      console.log('📥 API 回應內容:', JSON.stringify(result, null, 2));
    } catch (parseError) {
      console.error('❌ 無法解析 API 回應:', parseError);
      
      // 嘗試取得原始文字
      const text = await response.text();
      console.error('📄 原始回應內容:', text);
      
      return {
        success: false,
        message: `API 回應格式錯誤（HTTP ${response.status}）`,
        error_type: 'ParseError',
        error_details: text,
      };
    }

    // ✅ 如果 HTTP 狀態碼不是 200，但有 JSON 回應，顯示詳細錯誤
    if (!response.ok) {
      console.error('❌ API 回應錯誤:', {
        status: response.status,
        statusText: response.statusText,
        result: result,
      });
      
      return {
        success: false,
        message: result.message || `API 請求失敗（HTTP ${response.status}）`,
        error_type: result.error_type || 'HTTPError',
        error_details: result.error_details || response.statusText,
      };
    }

    return result;
  } catch (error) {
    console.error('❌ 呼叫儲值金扣款 API 失敗:', error);
    
    // ✅ 顯示更詳細的錯誤資訊
    if (error instanceof TypeError && error.message.includes('fetch')) {
      return {
        success: false,
        message: '無法連線到 API 伺服器，請檢查網路連線',
        error_type: 'NetworkError',
        error_details: error.message,
      };
    }
    
    return {
      success: false,
      message: error instanceof Error ? error.message : '呼叫儲值金扣款 API 失敗',
      error_type: error?.constructor?.name || 'UnknownError',
      error_details: error instanceof Error ? error.stack : String(error),
    };
  }
}

/**
 * 檢查客戶是否為儲值會員
 * @param customerId 客戶 ID
 */
export async function checkIsPrepaidMember(customerId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('customers')
      .select('member_level')
      .eq('id', customerId)
      .maybeSingle();

    if (error || !data) {
      return false;
    }

    // ✅ 修正：儲值會員的 member_level 是 'gold'
    return data.member_level === 'gold';
  } catch (error) {
    console.error('檢查儲值會員狀態失敗:', error);
    return false;
  }
}

/**
 * 取得客戶儲值帳戶餘額
 * @param customerId 客戶 ID
 */
export async function getPrepaidBalance(customerId: string) {
  try {
    const { data, error } = await supabase
      .from('prepaid_accounts')
      .select('real_balance, bonus_balance, total_balance, status')
      .eq('customer_id', customerId)
      .maybeSingle();

    if (error || !data) {
      return null;
    }

    return {
      realBalance: Number(data.real_balance),
      bonusBalance: Number(data.bonus_balance),
      totalBalance: Number(data.total_balance),
      status: data.status,
    };
  } catch (error) {
    console.error('取得儲值餘額失敗:', error);
    return null;
  }
}

/**
 * 檢查訂單是否已使用儲值金
 * @param orderId 訂單 ID
 */
export async function checkOrderPrepaidUsed(orderId: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from('orders')
      .select('prepaid_used')
      .eq('id', orderId)
      .maybeSingle();

    if (error || !data) {
      return false;
    }

    return data.prepaid_used === true;
  } catch (error) {
    console.error('檢查訂單儲值金使用狀態失敗:', error);
    return false;
  }
}
